/**
 * Analytics Settings Types
 *
 * Type definitions for project-level analytics integrations.
 * Supports multiple platforms: Amplitude, GA4, GTM, Mixpanel,
 * Facebook Pixel, TikTok Pixel, and Custom Scripts.
 *
 * @module analytics-settings/types
 */

// ============================================================================
// Platform Types
// ============================================================================

/**
 * Supported analytics platforms (client-side)
 */
export type ClientSideAnalyticsPlatform =
  | 'segmently_internal'
  | 'amplitude'
  | 'ga4'
  | 'gtm'
  | 'mixpanel'
  | 'posthog'
  | 'facebook_pixel'
  | 'tiktok_pixel'
  | 'custom_script';

/**
 * Supported server-side analytics platforms
 * Used for forwarding Stripe webhook events to analytics
 */
export type ServerSideAnalyticsPlatform =
  | 'facebook_capi'
  | 'ga4_measurement_protocol'
  | 'amplitude_server'
  | 'mixpanel_server'
  | 'posthog_server'
  | 'tiktok_events_api';

/**
 * All supported analytics platforms
 */
export type AnalyticsPlatform = ClientSideAnalyticsPlatform | ServerSideAnalyticsPlatform;

/**
 * Platform display information
 */
export interface PlatformInfo {
  id: AnalyticsPlatform;
  name: string;
  icon: string;
  description: string;
  docsUrl?: string;
}

/**
 * Platform metadata for UI
 */
export const PLATFORM_INFO: Record<AnalyticsPlatform, PlatformInfo> = {
  segmently_internal: {
    id: 'segmently_internal',
    name: 'Segmently Analytics',
    icon: '◎',
    description: 'First-party funnel analytics for Segmently Reports and canvas insights',
  },
  amplitude: {
    id: 'amplitude',
    name: 'Amplitude',
    icon: '📊',
    description: 'Product analytics platform for tracking user behavior',
    docsUrl: 'https://amplitude.com/docs',
  },
  ga4: {
    id: 'ga4',
    name: 'Google Analytics 4',
    icon: '📈',
    description: 'Google Analytics for web and app measurement',
    docsUrl: 'https://developers.google.com/analytics',
  },
  gtm: {
    id: 'gtm',
    name: 'Google Tag Manager',
    icon: '🏷️',
    description: 'Tag management system for marketing and analytics tags',
    docsUrl: 'https://developers.google.com/tag-manager',
  },
  mixpanel: {
    id: 'mixpanel',
    name: 'Mixpanel',
    icon: '🔬',
    description: 'Product analytics for user engagement tracking',
    docsUrl: 'https://docs.mixpanel.com',
  },
  posthog: {
    id: 'posthog',
    name: 'PostHog',
    icon: '🦔',
    description: 'Product analytics, session replay, and feature analytics',
    docsUrl: 'https://posthog.com/docs/libraries/js',
  },
  facebook_pixel: {
    id: 'facebook_pixel',
    name: 'Facebook Pixel',
    icon: '📘',
    description: 'Facebook advertising and conversion tracking',
    docsUrl: 'https://developers.facebook.com/docs/meta-pixel',
  },
  tiktok_pixel: {
    id: 'tiktok_pixel',
    name: 'TikTok Pixel',
    icon: '🎵',
    description: 'TikTok advertising and conversion tracking',
    docsUrl: 'https://ads.tiktok.com/help/article/tiktok-pixel',
  },
  custom_script: {
    id: 'custom_script',
    name: 'Custom Script',
    icon: '📜',
    description: 'Custom JavaScript for any analytics platform',
  },
  // Server-side platforms (for Stripe webhook forwarding)
  facebook_capi: {
    id: 'facebook_capi',
    name: 'Facebook Conversion API',
    icon: '📘',
    description: 'Server-side Facebook conversion tracking for payment events',
    docsUrl: 'https://developers.facebook.com/docs/marketing-api/conversions-api',
  },
  ga4_measurement_protocol: {
    id: 'ga4_measurement_protocol',
    name: 'GA4 Measurement Protocol',
    icon: '📈',
    description: 'Server-side GA4 event tracking for payment events',
    docsUrl: 'https://developers.google.com/analytics/devguides/collection/protocol/ga4',
  },
  amplitude_server: {
    id: 'amplitude_server',
    name: 'Amplitude (Server-Side)',
    icon: '📊',
    description: 'Server-side Amplitude tracking for revenue events',
    docsUrl: 'https://amplitude.com/docs/apis/analytics/http-v2',
  },
  mixpanel_server: {
    id: 'mixpanel_server',
    name: 'Mixpanel (Server-Side)',
    icon: '🔬',
    description: 'Server-side Mixpanel tracking for payment events',
    docsUrl: 'https://developer.mixpanel.com/reference/import-events',
  },
  posthog_server: {
    id: 'posthog_server',
    name: 'PostHog (Server-Side)',
    icon: '🦔',
    description: 'Server-side PostHog tracking for payment events',
    docsUrl: 'https://posthog.com/docs/api/capture',
  },
  tiktok_events_api: {
    id: 'tiktok_events_api',
    name: 'TikTok Events API',
    icon: '🎵',
    description: 'Server-side TikTok conversion tracking for payment events',
    docsUrl: 'https://ads.tiktok.com/help/article/events-api',
  },
};

// ============================================================================
// Platform Configurations
// ============================================================================

/**
 * Amplitude configuration
 */
export interface AmplitudeConfig {
  apiKey: string;
  serverZone?: 'US' | 'EU';
  trackSessionEvents?: boolean;
}

/**
 * Google Analytics 4 configuration
 */
export interface GA4Config {
  measurementId: string; // G-XXXXXXXXXX
  sendPageView?: boolean;
  debugMode?: boolean;
}

/**
 * Google Tag Manager configuration
 */
export interface GTMConfig {
  containerId: string; // GTM-XXXXXXX
  dataLayerName?: string; // default: 'dataLayer'
}

/**
 * Mixpanel configuration
 */
export interface MixpanelConfig {
  projectToken: string;
  trackPageViews?: boolean;
  persistence?: 'localStorage' | 'cookie';
  /**
   * Data residency region for the browser SDK `api_host`. Defaults to US.
   * US → api.mixpanel.com, EU → api-eu.mixpanel.com, IN → api-in.mixpanel.com.
   * India-residency projects REJECT events sent to the US host.
   * @see https://docs.mixpanel.com/docs/privacy/eu-residency
   * @see https://docs.mixpanel.com/docs/privacy/in-residency
   */
  region?: 'US' | 'EU' | 'IN';
}

/**
 * PostHog browser configuration
 */
export interface PostHogConfig {
  projectToken: string;
  host?: string;
  defaults?: string;
}

/** What the built-in Facebook Pixel adapter forwards.
 *  'standard' — only the 4 Meta standard events mapped from canonical events (default for new integrations).
 *  'all'      — standard events + every canonical event as fbq('trackCustom', <name>).
 *  'raw'      — only fbq('trackCustom', <name>) for every canonical event (legacy behavior; never offered in the UI).
 *  undefined  — treated as 'raw' by the runtime so existing funnels do not change. */
export type FacebookPixelEventMode = 'standard' | 'all' | 'raw';

/**
 * Facebook Pixel configuration
 */
export interface FacebookPixelConfig {
  pixelId: string;
  autoConfig?: boolean;
  eventMode?: FacebookPixelEventMode;
}

/**
 * TikTok Pixel configuration
 */
export interface TikTokPixelConfig {
  pixelId: string; // Found in TikTok Ads Manager (e.g., "CXXXXXXXXXXXXXXXXX")
  advancedMatching?: {
    email?: boolean;
    phone?: boolean;
  };
}

/**
 * Custom Script configuration
 * Allows injecting custom JavaScript in HEAD and/or BODY sections
 */
export interface CustomScriptConfig {
  name: string; // User-defined name for this script
  headScript?: string; // Script to inject in <head>
  bodyScript?: string; // Script to inject at end of <body>
}

/**
 * Early-journey flush rule for Segmently first-party analytics.
 * Events from the first N distinct screens of a journey are delivered
 * faster than the normal accumulation window.
 */
export interface SegmentlyInternalEarlyJourneyConfig {
  /** Number of distinct journey screens treated as early. 0 disables the tier. Default 2. */
  screens?: number;
  /** 'immediate' flushes early events on the next tick; 'interval' uses intervalMs. Default 'immediate'. */
  mode?: 'immediate' | 'interval';
  /** Accumulation window for early events when mode === 'interval'. */
  intervalMs?: number;
}

/**
 * Segmently first-party analytics configuration.
 * The integration enabled flag is the authoritative project-level opt-in.
 * Transport fields apply to HTTP mode only; invalid values fall back to
 * runtime defaults (validated in the WebShell adapter).
 */
export interface SegmentlyInternalAnalyticsConfig {
  mode?: 'http' | 'log' | 'memory';
  /** Accumulation window for normal events in ms. Default 5000, min 100. */
  flushIntervalMs?: number;
  /** Max events per ingest request (server cap 25). Default 25. */
  flushQueueSize?: number;
  /** 'retry' (default): backoff retries on transient failures. 'best_effort': single attempt. */
  deliveryMode?: 'best_effort' | 'retry';
  /** Canonical event names that flush immediately. Undefined = runtime defaults (purchase/buy-intent/exit). */
  immediateEvents?: string[];
  /** Early-journey flush rule for the first screens of a journey. */
  earlyJourney?: SegmentlyInternalEarlyJourneyConfig;
}

// ============================================================================
// Server-Side Platform Configurations
// ============================================================================

/**
 * Facebook Conversion API configuration
 * Requires Pixel ID + Access Token for server-side events
 *
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api
 */
export interface FacebookCAPIConfig {
  /** Facebook Pixel ID (same as client-side) */
  pixelId: string;
  /** System User Access Token (required for server-side) */
  accessToken: string;
  /** Optional: Test Event Code for Events Manager testing */
  testEventCode?: string;
  /**
   * Send subscription renewals as the platform's standard Purchase event.
   * Default (undefined) = true — current behavior. false → renewals go out as
   * the custom event "RecurringPayment" (same user matching, excluded from Purchase counts).
   */
  renewalsAsPurchase?: boolean;
}

/**
 * GA4 Measurement Protocol configuration
 * Requires Measurement ID + API Secret for server-side events
 *
 * @see https://developers.google.com/analytics/devguides/collection/protocol/ga4
 */
export interface GA4MeasurementProtocolConfig {
  /** GA4 Measurement ID (G-XXXXXXXXXX) */
  measurementId: string;
  /** API Secret from GA4 Admin → Data Streams → Measurement Protocol */
  apiSecret: string;
  /**
   * Send subscription renewals as the standard "purchase" event.
   * Default (undefined) = true — current behavior. false → renewals go out as
   * the custom event "recurring_payment".
   */
  renewalsAsPurchase?: boolean;
}

/**
 * Amplitude Server-Side configuration
 * Uses HTTP API V2 with API Key
 *
 * @see https://amplitude.com/docs/apis/analytics/http-v2
 */
export interface AmplitudeServerConfig {
  /** Amplitude API Key */
  apiKey: string;
  /** Server zone: US (api2.amplitude.com) or EU (api.eu.amplitude.com) */
  serverZone?: 'US' | 'EU';
}

/**
 * Mixpanel Server-Side configuration
 * Uses Import API; requires Service Account auth + numeric project_id query param.
 *
 * @see https://developer.mixpanel.com/reference/import-events
 */
export interface MixpanelServerConfig {
  /** Mixpanel Project Token (still kept on the integration for client-side parity) */
  projectToken: string;
  /** Numeric Mixpanel Project ID — REQUIRED as ?project_id=… query param on /import */
  projectId: string;
  /** Service Account username — REQUIRED for /import Basic Auth */
  serviceAccountUsername: string;
  /** Service Account secret — REQUIRED for /import Basic Auth */
  serviceAccountSecret: string;
  /** Server zone: US (api.mixpanel.com), EU (api-eu.mixpanel.com), or IN (api-in.mixpanel.com). Defaults to US. */
  region?: 'US' | 'EU' | 'IN';
}

/**
 * PostHog Server-Side configuration
 * Uses Capture API with project token.
 *
 * @see https://posthog.com/docs/api/capture
 */
export interface PostHogServerConfig {
  /** PostHog Project API Key / project token, usually starts with phc_ */
  projectToken: string;
  /** Capture API host, e.g. https://us.i.posthog.com or https://eu.i.posthog.com */
  host?: string;
  /** Optional SDK defaults snapshot date, kept for parity with browser config */
  defaults?: string;
}

/**
 * TikTok Events API configuration
 * Requires Pixel Code + Access Token for server-side events
 *
 * @see https://ads.tiktok.com/help/article/events-api
 */
export interface TikTokEventsAPIConfig {
  /** TikTok Pixel Code (same as client-side pixelId, e.g., "CXXXXXXXXXXXXXXXXX") */
  pixelCode: string;
  /** System User Access Token from TikTok Business Center */
  accessToken: string;
  /** Optional: Test Event Code for TikTok Events Manager testing */
  testEventCode?: string;
  /**
   * Send subscription renewals as TikTok's standard "CompletePayment" event.
   * Default (undefined) = true — current behavior. false → renewals go out as
   * the custom event "RecurringPayment" (Events API accepts custom names).
   */
  renewalsAsPurchase?: boolean;
}

/**
 * Union type for all platform configurations
 */
export type PlatformConfig =
  // Client-side configs
  | SegmentlyInternalAnalyticsConfig
  | AmplitudeConfig
  | GA4Config
  | GTMConfig
  | MixpanelConfig
  | PostHogConfig
  | FacebookPixelConfig
  | TikTokPixelConfig
  | CustomScriptConfig
  // Server-side configs
  | FacebookCAPIConfig
  | GA4MeasurementProtocolConfig
  | AmplitudeServerConfig
  | MixpanelServerConfig
  | PostHogServerConfig
  | TikTokEventsAPIConfig;

// ============================================================================
// Analytics Integration
// ============================================================================

/**
 * Analytics integration instance
 */
export interface AnalyticsIntegration {
  id: string;
  platform: AnalyticsPlatform;
  name: string; // Display name (e.g., "My GA4", "Facebook Conversions")
  enabled: boolean;
  config: PlatformConfig;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create a new integration with defaults
 */
export function createIntegration(
  platform: AnalyticsPlatform,
  name: string,
  config: PlatformConfig
): Omit<AnalyticsIntegration, 'id'> {
  return {
    platform,
    name,
    enabled: true,
    config,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Get default config for a platform
 */
export function getDefaultConfig(platform: AnalyticsPlatform): PlatformConfig {
  switch (platform) {
    // Client-side platforms
    case 'segmently_internal':
      return { mode: 'http' };
    case 'amplitude':
      return { apiKey: '', serverZone: 'US', trackSessionEvents: true };
    case 'ga4':
      return { measurementId: '', sendPageView: true, debugMode: false };
    case 'gtm':
      return { containerId: '', dataLayerName: 'dataLayer' };
    case 'mixpanel':
      return { projectToken: '', trackPageViews: true, persistence: 'localStorage', region: 'US' };
    case 'posthog':
      return { projectToken: '', host: 'https://us.i.posthog.com', defaults: '2026-01-30' };
    case 'facebook_pixel':
      return { pixelId: '', autoConfig: true, eventMode: 'standard' };
    case 'tiktok_pixel':
      return { pixelId: '' };
    case 'custom_script':
      return { name: 'Custom Script', headScript: '', bodyScript: '' };
    // Server-side platforms
    case 'facebook_capi':
      return { pixelId: '', accessToken: '', testEventCode: '', renewalsAsPurchase: true };
    case 'ga4_measurement_protocol':
      return { measurementId: '', apiSecret: '', renewalsAsPurchase: true };
    case 'amplitude_server':
      return { apiKey: '', serverZone: 'US' };
    case 'mixpanel_server':
      return {
        projectToken: '',
        projectId: '',
        serviceAccountUsername: '',
        serviceAccountSecret: '',
        region: 'US',
      };
    case 'posthog_server':
      return { projectToken: '', host: 'https://us.i.posthog.com', defaults: '2026-01-30' };
    case 'tiktok_events_api':
      return { pixelCode: '', accessToken: '', testEventCode: '', renewalsAsPurchase: true };
  }
}

// ============================================================================
// Consent Management
// ============================================================================

/**
 * GDPR consent management configuration
 */
export interface ConsentManagementConfig {
  enabled: boolean;
  bannerText?: string;
  privacyPolicyUrl?: string;
  defaultConsent: 'granted' | 'denied';
}

/**
 * Default consent configuration
 */
export const DEFAULT_CONSENT_CONFIG: ConsentManagementConfig = {
  enabled: true,
  bannerText: 'We use cookies to improve your experience and analyze site usage.',
  privacyPolicyUrl: '',
  defaultConsent: 'denied',
};

// ============================================================================
// Project Analytics Settings
// ============================================================================

/**
 * Complete project analytics settings
 * Stored in Firestore: projects/{projectId}/settings/analytics
 */
export interface ProjectAnalyticsSettings {
  consentManagement: ConsentManagementConfig;
  integrations: AnalyticsIntegration[];
}

/**
 * Default project analytics settings
 */
export const DEFAULT_ANALYTICS_SETTINGS: ProjectAnalyticsSettings = {
  consentManagement: DEFAULT_CONSENT_CONFIG,
  integrations: [],
};

// ============================================================================
// Type Guards (Client-Side)
// ============================================================================

export function isAmplitudeConfig(config: PlatformConfig): config is AmplitudeConfig {
  return 'apiKey' in config && 'trackSessionEvents' in config;
}

export function isSegmentlyInternalAnalyticsConfig(
  config: PlatformConfig
): config is SegmentlyInternalAnalyticsConfig {
  return 'mode' in config || Object.keys(config).length === 0;
}

export function isGA4Config(config: PlatformConfig): config is GA4Config {
  return 'measurementId' in config && 'sendPageView' in config;
}

export function isGTMConfig(config: PlatformConfig): config is GTMConfig {
  return 'containerId' in config;
}

export function isMixpanelConfig(config: PlatformConfig): config is MixpanelConfig {
  return 'projectToken' in config && 'trackPageViews' in config;
}

export function isPostHogConfig(config: PlatformConfig): config is PostHogConfig {
  return 'projectToken' in config && 'defaults' in config && !('projectId' in config);
}

export function isFacebookPixelConfig(config: PlatformConfig): config is FacebookPixelConfig {
  return 'pixelId' in config && 'autoConfig' in config;
}

export function isTikTokPixelConfig(config: PlatformConfig): config is TikTokPixelConfig {
  return 'pixelId' in config && 'advancedMatching' in config;
}

export function isCustomScriptConfig(config: PlatformConfig): config is CustomScriptConfig {
  return 'headScript' in config || 'bodyScript' in config;
}

// ============================================================================
// Type Guards (Server-Side)
// ============================================================================

export function isFacebookCAPIConfig(config: PlatformConfig): config is FacebookCAPIConfig {
  return 'pixelId' in config && 'accessToken' in config;
}

export function isGA4MeasurementProtocolConfig(
  config: PlatformConfig
): config is GA4MeasurementProtocolConfig {
  return 'measurementId' in config && 'apiSecret' in config;
}

export function isAmplitudeServerConfig(config: PlatformConfig): config is AmplitudeServerConfig {
  return 'apiKey' in config && !('trackSessionEvents' in config);
}

export function isMixpanelServerConfig(config: PlatformConfig): config is MixpanelServerConfig {
  return 'projectToken' in config && 'projectId' in config && !('trackPageViews' in config);
}

export function isPostHogServerConfig(config: PlatformConfig): config is PostHogServerConfig {
  return 'projectToken' in config && 'defaults' in config && !('projectId' in config);
}

export function isTikTokEventsAPIConfig(config: PlatformConfig): config is TikTokEventsAPIConfig {
  return 'pixelCode' in config && 'accessToken' in config;
}

/**
 * Check if platform is a server-side platform
 */
export function isServerSidePlatform(platform: AnalyticsPlatform): platform is ServerSideAnalyticsPlatform {
  return [
    'facebook_capi',
    'ga4_measurement_protocol',
    'amplitude_server',
    'mixpanel_server',
    'posthog_server',
    'tiktok_events_api',
  ].includes(platform);
}

/**
 * List of all server-side platform types
 */
export const SERVER_SIDE_PLATFORMS: ServerSideAnalyticsPlatform[] = [
  'facebook_capi',
  'ga4_measurement_protocol',
  'amplitude_server',
  'mixpanel_server',
  'posthog_server',
  'tiktok_events_api',
];
